import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { RunArtifactCard } from '@/pages/schedules/components/run-artifact-card';
import { formatRunCost } from '@/utils/format-cost';
import { useListArtifacts } from '@/hooks/queries/artifacts/use-list-artifacts';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useOrgStore } from '@/stores/org-store';
import { ROUTES } from '@/constants/routes';
import { formatDateTime } from '@/utils/format-date';
import type { ScheduledRun } from '@/types/schedules';

interface RunDetailPanelProps {
  run: ScheduledRun | null;
}

const formatRunTime = (seconds: number | null): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}`;
  return `${Math.round(seconds / 60)}`;
};

const runTimeUnit = (seconds: number | null): string | undefined => {
  if (seconds == null) return undefined;
  if (seconds < 60) {
    return Math.round(seconds) === 1 ? 'second' : 'seconds';
  }
  return Math.round(seconds / 60) === 1 ? 'minute' : 'minutes';
};

function MetricCard({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <div className='flex min-w-[107px] flex-col gap-2 rounded-md bg-accent p-3'>
      <p className='text-xs leading-none text-foreground'>{label}</p>
      <p className='flex items-end text-base font-bold leading-none'>
        {value}
        {unit && (
          <span className='ml-0.5 text-xs font-normal text-muted-foreground'>
            {unit}
          </span>
        )}
      </p>
    </div>
  );
}

export function RunDetailPanel({ run }: RunDetailPanelProps) {
  const { currentOrg } = useOrgStore();
  const { isScheduleRunCostEnabled } = useLaunchDarkly();
  const artifactsQuery = useListArtifacts(
    run ? { source_run_id: run.id, limit: 50 } : {},
    { enabled: !!run },
  );

  if (!run) {
    return (
      <div className='flex h-full flex-1 items-center justify-center'>
        <p className='text-sm text-muted-foreground'>
          Select a run to view its details.
        </p>
      </div>
    );
  }

  const artifacts = artifactsQuery.data?.artifacts ?? [];
  const timestamp = run.started_at ?? run.created_at;

  return (
    <div className='subtle-scrollbar @container flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-background'>
      <div className='flex min-w-0 flex-col gap-3 p-6 pb-0'>
        <div className='flex flex-col gap-1'>
          <h1 className='text-base font-semibold leading-6'>
            {run.workflow_name}
          </h1>
          {run.outcome_summary && (
            <p className='text-base leading-6 text-foreground'>
              {run.outcome_summary}
            </p>
          )}
        </div>
        <div className='flex items-center gap-3 text-sm text-muted-foreground'>
          {run.schedule_name && (
            <>
              <span>{run.schedule_name}</span>
              <span className='size-0.5 rounded-full bg-muted-foreground' />
            </>
          )}
          {timestamp && <span>{formatDateTime(timestamp)}</span>}
        </div>
      </div>

      <div className='flex items-center justify-between gap-4 p-6'>
        <div className='flex gap-2'>
          {isScheduleRunCostEnabled && (
            <MetricCard label='Cost' value={formatRunCost(run.cost) ?? '—'} />
          )}
          <MetricCard
            label='Run Time'
            value={formatRunTime(run.duration_seconds)}
            unit={runTimeUnit(run.duration_seconds)}
          />
        </div>
        <Button
          variant='outline'
          size='sm'
          className='shrink-0'
          onClick={() =>
            window.open(
              `/o/${currentOrg?.orgid}${ROUTES.WORKFLOW.ROOT}/${run.workflow_id}`,
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          <ArrowUpRight className='size-4' /> View Workflow
        </Button>
      </div>

      <Separator className='my-6' />

      <div className='px-6 pb-8'>
        <h2 className='mb-4 text-base font-semibold'>
          {artifacts.length} {artifacts.length === 1 ? 'Artifact' : 'Artifacts'}
        </h2>
        {artifactsQuery.isLoading && (
          <div className='grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4'>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className='h-[202px] rounded-xl' />
            ))}
          </div>
        )}
        {artifactsQuery.isError && (
          <p className='text-sm text-muted-foreground'>
            Failed to load artifacts.
          </p>
        )}
        {!artifactsQuery.isLoading &&
          !artifactsQuery.isError &&
          artifacts.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              This run produced no artifacts.
            </p>
          )}
        <div className='grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4'>
          {artifacts.map((artifact) => (
            <RunArtifactCard key={artifact.id} artifact={artifact} />
          ))}
        </div>
      </div>
    </div>
  );
}
